import { useMemo } from "react";
import { BarChart3 } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Card } from "@/shared/ui/DesignLib";

type FlowTrade = {
  ticker?: string;
  symbol?: string;
  c_p?: string;
  bur_or_sell?: string;
  prem_price?: number;
  size?: number;
  total_volume?: number;
};

type PivotRow = {
  ticker: string;
  trades: number;
  premium: number;
  contracts: number;
  calls: number;
  puts: number;
  bought: number;
  sold: number;
};

const formatCompactMoney = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const formatCompactNumber = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
};

const pct = (part: number, total: number) => {
  if (!total) return 0;
  return Math.round((part / total) * 100);
};

const FlowPivotTable = ({ trades, title = "Pivot Table" }: { trades: FlowTrade[]; title?: string }) => {
  const { rows, totals } = useMemo(() => {
    const rowMap = new Map<string, PivotRow>();

    trades.forEach((trade) => {
      const ticker = (trade.ticker || trade.symbol || "UNKNOWN").toUpperCase();
      const existing = rowMap.get(ticker) || {
        ticker,
        trades: 0,
        premium: 0,
        contracts: 0,
        calls: 0,
        puts: 0,
        bought: 0,
        sold: 0,
      };

      existing.trades += 1;
      existing.premium += Number(trade.prem_price || 0);
      existing.contracts += Number(trade.total_volume || trade.size || 0);
      if (trade.c_p === "C") existing.calls += 1;
      if (trade.c_p === "P") existing.puts += 1;
      if (trade.bur_or_sell === "BOUGHT") existing.bought += 1;
      if (trade.bur_or_sell === "SOLD") existing.sold += 1;
      rowMap.set(ticker, existing);
    });

    const rows = [...rowMap.values()]
      .sort((a, b) => b.premium - a.premium || b.trades - a.trades)
      .slice(0, 10);

    const totals = [...rowMap.values()].reduce(
      (acc, row) => ({
        ticker: "TOTAL",
        trades: acc.trades + row.trades,
        premium: acc.premium + row.premium,
        contracts: acc.contracts + row.contracts,
        calls: acc.calls + row.calls,
        puts: acc.puts + row.puts,
        bought: acc.bought + row.bought,
        sold: acc.sold + row.sold,
      }),
      {
        ticker: "TOTAL",
        trades: 0,
        premium: 0,
        contracts: 0,
        calls: 0,
        puts: 0,
        bought: 0,
        sold: 0,
      }
    );

    return { rows, totals };
  }, [trades]);

  if (!trades.length) return null;

  return (
    <Card>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-bold text-foreground">{title}</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Top tickers by premium across the active search and filters.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="font-sans text-[10px] uppercase">
              {formatCompactMoney(totals.premium)} premium
            </Badge>
            <Badge variant="outline" className="font-sans text-[10px] uppercase">
              {formatCompactNumber(totals.contracts)} contracts
            </Badge>
            <Badge variant="outline" className="font-sans text-[10px] uppercase">
              {totals.trades.toLocaleString()} trades
            </Badge>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 border-border/50 hover:bg-muted/30">
                <TableHead className="pl-3 text-[10px] font-bold uppercase tracking-wider">Ticker</TableHead>
                <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider">Trades</TableHead>
                <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider">Premium</TableHead>
                <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider">Contracts</TableHead>
                <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider">Call/Put</TableHead>
                <TableHead className="pr-3 text-right text-[10px] font-bold uppercase tracking-wider">Bought/Sold</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.ticker} className="border-border/40 hover:bg-muted/20">
                  <TableCell className="pl-3 font-sans text-[12px] font-bold text-primary">{row.ticker}</TableCell>
                  <TableCell className="text-right font-sans text-[12px]">{row.trades.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-sans text-[12px] font-bold">{formatCompactMoney(row.premium)}</TableCell>
                  <TableCell className="text-right font-sans text-[12px] text-muted-foreground">{formatCompactNumber(row.contracts)}</TableCell>
                  <TableCell className="text-right font-sans text-[12px] text-muted-foreground">
                    {pct(row.calls, row.trades)}% / {pct(row.puts, row.trades)}%
                  </TableCell>
                  <TableCell className="pr-3 text-right font-sans text-[12px] text-muted-foreground">
                    {pct(row.bought, row.trades)}% / {pct(row.sold, row.trades)}%
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="border-border/60 bg-muted/20 hover:bg-muted/20">
                <TableCell className="pl-3 font-sans text-[11px] font-bold text-foreground">TOTAL</TableCell>
                <TableCell className="text-right font-sans text-[11px] font-bold">{totals.trades.toLocaleString()}</TableCell>
                <TableCell className="text-right font-sans text-[11px] font-bold">{formatCompactMoney(totals.premium)}</TableCell>
                <TableCell className="text-right font-sans text-[11px] font-bold">{formatCompactNumber(totals.contracts)}</TableCell>
                <TableCell className="text-right font-sans text-[11px] font-bold">
                  {pct(totals.calls, totals.trades)}% / {pct(totals.puts, totals.trades)}%
                </TableCell>
                <TableCell className="pr-3 text-right font-sans text-[11px] font-bold">
                  {pct(totals.bought, totals.trades)}% / {pct(totals.sold, totals.trades)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </Card>
  );
};

export default FlowPivotTable;
